
import React, { useCallback, useEffect, useMemo } from 'react'
import { type NodeProps, useNodeConnections, useNodesData, useUpdateNodeInternals } from '@xyflow/react'
import { ShaderNode } from '@/components/canvas/nodes/variants/ShaderNode'
import { type GenieNodeState, NODE_DIMENSIONS } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { shaderHandles } from '@/lib/canvas/configs/handles'
import { connectedShaderInputTypes, resolveShaderInputs } from '@/components/canvas/nodes/shader/resolveShaderInputs'
import { useShaderGenerationScope } from '@/components/canvas/shader/ShaderGenerationScope'
import { publishWiredShaderInputs } from '@/components/canvas/shader/wired-inputs'
import type { HandleMeta } from '@/lib/canvas/types'
import type { ShaderInputType } from '@/lib/canvas/shader/media/contract'

export interface ShaderNodeNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

function ShaderNodeNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as ShaderNodeNodeData
  const { state: genieState, onHoverChange } = nodeData
  const onStateChange = useNodeStateChange(id)

  const handleSelect = useCallback(() => {}, [])

  // Upstream media, per input handle, read reactively.
  const incomingConnections = useNodeConnections({ handleType: 'target' })
  const sourceNodeIds = useMemo(
    () => incomingConnections.map((c) => c.source),
    [incomingConnections],
  )
  const sourceNodesData = useNodesData(sourceNodeIds)
  const inputs = useMemo(
    () => resolveShaderInputs(incomingConnections, sourceNodesData),
    [incomingConnections, sourceNodesData],
  )
  // A stable array while the set of wired inputs is unchanged, so the memoised node doesn't re-render.
  const connectedKey = connectedShaderInputTypes(incomingConnections).join('|')
  const connectedInputs = useMemo<ShaderInputType[]>(
    () => (connectedKey ? (connectedKey.split('|') as ShaderInputType[]) : []),
    [connectedKey],
  )
  // Node controls show which inputs are wired; they render outside React Flow's store on the web canvas.
  const scope = useShaderGenerationScope()
  useEffect(() => publishWiredShaderInputs(scope, id, connectedInputs), [scope, id, connectedInputs])

  const shaderProps = useMemo(
    () => ({
      id,
      selected: selected || genieState.selected,
      width: NODE_DIMENSIONS.ShaderNode.width,
      height: NODE_DIMENSIONS.ShaderNode.height,
      onSelect: handleSelect,
      onHoverChange,
      onStateChange,
      state: genieState as Record<string, unknown>,
      inputs,
      connectedInputs,
    }),
    [id, genieState, selected, handleSelect, onHoverChange, onStateChange, inputs, connectedInputs],
  )

  // Handles are derived, not read from persisted node data — persisted handles
  // can carry inputs from an earlier build or from the generic model flow. An
  // empty shader offers every input; one with code offers what it declares,
  // plus anything already wired so no edge is left dangling.
  const shaderState = genieState as Record<string, unknown>
  const sketchId = typeof shaderState.sketchId === 'string' ? shaderState.sketchId : undefined
  const inputTypes = Array.isArray(shaderState.inputTypes) ? (shaderState.inputTypes as unknown[]) : undefined
  const hasCode = typeof shaderState.code === 'string' && shaderState.code.trim().length > 0
  const handles: HandleMeta[] = useMemo(
    () => shaderHandles({ sketchId, inputTypes, connected: connectedInputs, hasCode }),
    [sketchId, inputTypes, connectedInputs, hasCode],
  )

  // Swapping shaders can change how many handles there are; React Flow won't
  // re-measure on its own, so edges would keep pointing at stale positions.
  const updateNodeInternals = useUpdateNodeInternals()
  const handleSignature = handles.map((h) => h.id).join('|')
  useEffect(() => {
    updateNodeInternals(id)
  }, [id, handleSignature, updateNodeInternals])

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <ShaderNode {...shaderProps} />
    </>
  )
}

/** State fields the node face reads; a change to any other field doesn't re-render it. */
const RENDERED_STATE_KEYS = [
  'title',
  'collapsed',
  'selected',
  'code',
  'runtimeMode',
  'previewPaused',
  'paramValues',
  'extractedParams',
  'sketchId',
  'inputTypes',
  'prompt',
  'generation',
  'provider',
  'promptHistory',
] as const

export const ShaderNodeNode = React.memo(
  ShaderNodeNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as ShaderNodeNodeData
    const nextData = nextProps.data as unknown as ShaderNodeNodeData
    const prevState = prevData.state as Record<string, unknown>
    const nextState = nextData.state as Record<string, unknown>

    return (
      RENDERED_STATE_KEYS.every((key) => prevState[key] === nextState[key]) &&
      prevData.onHoverChange === nextData.onHoverChange &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

ShaderNodeNode.displayName = 'ShaderNodeNode'
